import { all, call, put, select, takeEvery } from 'redux-saga/effects';
import { tableControlActions } from './tableControlSlice';
import * as aiqConfig from 'config';
import { notificationHandler } from 'utils/utils';
import { selectTableConfigFetchingInProgress, selectTableConfigByKey, selectAllTablesData, selectTableControlSlice } from './tableControlSelectors';
import { axiosAiq } from 'config';
import { transactionTableConfig, configCacheKey } from './transactionTablesConfig';

// Exported for tests.
export function* fetchTableData({
    payload: {
        tableId,
        locationId,
        urlPath,
        pageNum = 0,
        size = aiqConfig.locationTransactionsTableRowsPerPage,
        append = false,
        fetchColumnsConfig = true,
        fetchLocationAnnouncements,
        useCache = false,
    },
}) {
    // The allEvents saga owns data fetching for the "all locations" tables
    // (All Events + Manual Departures/announcements). Skip them here to avoid
    // a duplicate fetch that would overwrite the parametrized result.
    if (tableId === transactionTableConfig.allLocationsTransactions.tableId || tableId === transactionTableConfig.allLocationsAnnouncements.tableId) {
        return;
    }

    try {
        if (useCache && !append) {
            const allTablesData = yield select(selectAllTablesData);
            const existingData = allTablesData?.[tableId]?.[pageNum];
            if (existingData?.data) {
                yield put(
                    tableControlActions.fetchTableDataSuccess({
                        data: existingData.data,
                        tableId,
                        pageNum,
                        append: false,
                    })
                );
                return;
            }
        }

        let dataUrlPath;
        let configUrlPath;
        let configName;
        let getLocationAnnouncements = false;
        let skipDataFetch = false;

        if (!urlPath) {
            ({ dataUrlPath, configUrlPath, configName, getLocationAnnouncements, skipDataFetch } = yield call(
                readTableConfig,
                tableId,
                fetchLocationAnnouncements
            ));
            dataUrlPath = dataUrlPath(pageNum, size, locationId);
        } else {
            dataUrlPath = urlPath;
        }

        const response = skipDataFetch ? { data: null } : yield axiosAiq.get(dataUrlPath);

        // No configName for singleLocationTransactions: the history table fetches its own
        // per-location columns through ensureHistoryColumnsConfig.
        fetchColumnsConfig && configName && (yield call(ensureColumnsConfig, configUrlPath(locationId), configName, locationId));

        if (!skipDataFetch) {
            yield put(
                tableControlActions.fetchTableDataSuccess({
                    data: response?.data,
                    tableId,
                    pageNum,
                    append,
                })
            );
        } else {
            yield put(
                tableControlActions.fetchTableDataSuccess({
                    data: [],
                    tableId,
                    pageNum,
                    append,
                })
            );
        }

        if (getLocationAnnouncements) {
            yield put(
                tableControlActions.fetchTableDataStart({
                    tableId: `${locationId}_manualAnnouncements`,
                    locationId,
                    pageNum,
                    size,
                    append,
                })
            );
        }
    } catch (err) {
        yield call(notificationHandler, {
            err,
            action: tableControlActions.fetchTableDataFailed,
            title: 'Error fetching table data',
        });
    }
}

/**
 * Fetch a table's column configuration once per (configName, locationId), and store it under that
 * key. Shared with allEventsSagas so the two fetch-once implementations cannot drift apart again.
 *
 * The key must include the location: the backend returns `name: 'ALL'` for the transactions columns
 * regardless of which location was requested, so a location-independent key served location A's
 * captions to every other location, with the guard below preventing any refetch.
 */
export function* ensureColumnsConfig(tableConfigUrlPath, configName, locationId) {
    const configKey = configCacheKey(configName, locationId);
    try {
        const [configKeyFetchingInProgress, tableConfig] = yield all([
            select(selectTableConfigFetchingInProgress),
            select(selectTableConfigByKey(configKey)),
        ]);
        if (tableConfig || configKeyFetchingInProgress === configKey) return;

        yield put(tableControlActions.fetchColumnsConfigStart({ configKey }));
        const response = yield axiosAiq.get(tableConfigUrlPath);
        yield put(tableControlActions.fetchColumnsConfigSuccess({ configKey, columns: response.data.columns }));
    } catch (err) {
        yield call(notificationHandler, {
            err,
            action: tableControlActions.fetchColumnsConfigFailed,
            title: 'Error fetching table configuration',
        });
    }
}

// Automatic re-attempts toast again once the last toast is older than this.
export const HISTORY_CONFIG_REPEAT_TOAST_MS = 5 * 60 * 1000;

const normalizeHistoryColumns = (data) => {
    if (Array.isArray(data)) return data;
    return Array.isArray(data?.columns) ? data.columns : [];
};

/**
 * Fetch the history table's column config once per location. Concurrent requests for one
 * location dedupe on the fetching flag, which the Start put sets before the GET yields.
 *
 * Automatic re-attempts (such as re-opening the History tab) toast at most once per
 * HISTORY_CONFIG_REPEAT_TOAST_MS and otherwise rely on the inline banner. Explicit retries
 * always toast.
 */
export function* ensureHistoryColumnsConfig({ payload: { locationId, userInitiated = false } }) {
    if (!locationId) return;
    const slice = yield select(selectTableControlSlice);
    if (slice?.historyColumnsConfig?.[locationId] || slice?.historyColumnsConfigFetching?.[locationId]) return;
    const lastNotifiedAt = slice?.historyColumnsConfigError?.[locationId]?.notifiedAt ?? null;

    yield put(tableControlActions.fetchHistoryColumnsConfigStart({ locationId }));
    try {
        const response = yield axiosAiq.get(transactionTableConfig.singleLocationTransactions.historyColumnsUrlPath(locationId));
        const columns = normalizeHistoryColumns(response?.data);
        // An empty list would cache as a valid config: a blank grid with no error and no retry.
        if (columns.length === 0) throw new Error('The column configuration for this location came back empty.');
        yield put(tableControlActions.fetchHistoryColumnsConfigSuccess({ locationId, columns }));
    } catch (err) {
        const now = Date.now();
        const notify = userInitiated || lastNotifiedAt === null || now - lastNotifiedAt >= HISTORY_CONFIG_REPEAT_TOAST_MS;
        yield put(
            tableControlActions.fetchHistoryColumnsConfigFailed({
                locationId,
                error: { message: err?.message || '', code: err?.code || '', notifiedAt: notify ? now : lastNotifiedAt },
            })
        );
        if (notify) yield call(notificationHandler, { err, title: 'Error fetching table configuration' });
    }
}

function readTableConfig(tableId, fetchLocationAnnouncements) {
    let tableType;
    let getLocationAnnouncements = false;

    switch (true) {
        case tableId === transactionTableConfig.allLocationsTransactions.tableId:
            tableType = 'allLocationsTransactions';
            break;
        case tableId === transactionTableConfig.allLocationsAnnouncements.tableId:
            tableType = 'allLocationsAnnouncements';
            break;
        case typeof tableId === 'string' && tableId.endsWith('_manualAnnouncements'):
            tableType = 'singleLocationAnnouncements';
            break;
        default:
            tableType = 'singleLocationTransactions';
            fetchLocationAnnouncements && (getLocationAnnouncements = true);
    }

    const { dataUrlPath, configUrlPath, configName } = transactionTableConfig[tableType];

    // The history table fetches its own rows and columns. For singleLocationTransactions this
    // path only primes manual announcements and writes the `allTablesData[locationId]` entry
    // that transactionSplitViewMode.js reads as "this location has been primed".
    const skipDataFetch = tableType === 'singleLocationTransactions';

    return { dataUrlPath, configUrlPath, configName, getLocationAnnouncements, skipDataFetch };
}

function* onFetchTableDataStart() {
    yield takeEvery(tableControlActions.fetchTableDataStart.type, fetchTableData);
}
function* onFetchHistoryColumnsConfigRequest() {
    yield takeEvery(tableControlActions.fetchHistoryColumnsConfigRequest.type, ensureHistoryColumnsConfig);
}
export function* tableControlSaga() {
    yield all([call(onFetchTableDataStart), call(onFetchHistoryColumnsConfigRequest)]);
}
